import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * StarRating — dual mode.
 *
 *   Read-only: show the current average + count (used on cards and detail headers).
 *     <StarRating avg={4.2} count={17} />
 *
 *   Interactive: buyer clicks to rate. Fires POST when kind + id + canRate are set.
 *     <StarRating avg={4.2} count={17} kind="character" id={char.id} canRate={char.has_purchased} onRated={fn} />
 *
 * Renders nothing when there are 0 ratings AND the user cannot rate — keeps
 * empty state uncluttered on Browse/Vault cards.
 */
export default function StarRating({ avg = 0, count = 0, kind, id, canRate, onRated, size = "sm" }) {
  const [mine, setMine] = useState(null); // 1..5 or null
  const [busy, setBusy] = useState(false);
  const [hover, setHover] = useState(0);

  useEffect(() => {
    if (canRate && kind && id) {
      api.get(`/${kind === "character" ? "characters" : "works"}/${id}/rating/mine`)
        .then(({ data }) => setMine(data.stars))
        .catch(() => setMine(null));
    }
  }, [canRate, kind, id]);

  const rate = async (n) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/${kind === "character" ? "characters" : "works"}/${id}/rating`, { stars: n });
      setMine(n);
      toast.success(`You rated this ${n}★`);
      onRated?.(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const cls = size === "sm" ? "w-3.5 h-3.5" : "w-5 h-5";
  const displayFilled = hover || mine || avg;
  const isInteractive = canRate && kind && id;

  if (!isInteractive && count === 0) return null;

  return (
    <div className="inline-flex items-center gap-1.5" data-testid={`star-rating-${kind || "readonly"}`}>
      <div className={`flex ${isInteractive ? "gap-0.5" : "gap-px"}`}>
        {[1, 2, 3, 4, 5].map((n) => {
          const filled = n <= displayFilled;
          const half = !filled && n - 0.5 <= displayFilled;
          return (
            <button
              key={n}
              type="button"
              disabled={!isInteractive || busy}
              onMouseEnter={() => isInteractive && setHover(n)}
              onMouseLeave={() => isInteractive && setHover(0)}
              onClick={() => isInteractive && rate(n)}
              className={`${isInteractive ? "hover:scale-110 transition-transform cursor-pointer" : "cursor-default"} disabled:cursor-default`}
              aria-label={`${n} star${n === 1 ? "" : "s"}`}
              data-testid={isInteractive ? `star-rating-btn-${n}` : undefined}
            >
              <Star className={`${cls} ${filled ? "fill-[#E6B860] text-[#E6B860]" : half ? "fill-[#E6B860]/50 text-[#E6B860]" : "text-[#6E675E]"}`} />
            </button>
          );
        })}
      </div>
      {count > 0 && (
        <span className="text-[10px] uppercase tracking-widest text-[#A8A095] font-mono-brass">
          {avg?.toFixed?.(1) ?? "—"} · <span className="text-[#6E675E]">{count}</span>
        </span>
      )}
      {isInteractive && count === 0 && !mine && (
        <span className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass">Be the first</span>
      )}
    </div>
  );
}
